import type { JSX, KeyboardEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Section } from '../VueDAigle/composants.js';
import { useFormats } from '../VueDAigle/formats.js';
import { maximumRond } from '../VueDAigle/graphiques.js';
import { MA_PART, RESTE, styleRang } from './donnees.js';
import type { BarreMois, SegmentBarre } from './donnees.js';
import styles from './Comptabilite.module.css';
import { useLargeur } from './useLargeur.js';

const GRADUATIONS = [0, 0.25, 0.5, 0.75, 1] as const;
/** Une colonne reste assez large pour le doigt : en dessous, le graphique défile. */
const BANDE_MIN = 44;

/**
 * Les douze mois, en barres empilées par partie (provisoire en clair). Un mois
 * est un bouton : il devient le mois affiché. Sur téléphone le graphique
 * défile de côté plutôt que d'écraser des colonnes sous la taille d'un doigt.
 */
export function Historique({
  barres,
  selection,
  onChoisir,
  associe,
  numero,
}: {
  barres: BarreMois[];
  selection: string;
  onChoisir: (mois: string) => void;
  associe: boolean;
  numero: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const [defilant, largeur] = useLargeur<HTMLDivElement>(true);
  const [survol, setSurvol] = useState<string | null>(null);

  const H = 280;
  const mG = 48;
  const mD = 8;
  const mH = 10;
  const mB = 40;
  const W = Math.max(largeur, mG + mD + barres.length * BANDE_MIN);
  const bande = (W - mG - mD) / Math.max(barres.length, 1);
  const largeurBarre = Math.min(bande * 0.66, 32);
  const haut = maximumRond(Math.max(1, ...barres.map((b) => b.htCentimes)));
  const hauteurUtile = H - mH - mB;
  const y = (valeur: number): number => mH + hauteurUtile - (valeur / haut) * hauteurUtile;

  const nomSegment = (segment: SegmentBarre): string => {
    if (segment.id === MA_PART) return t('comptabilite.historique.maPart');
    if (segment.id === RESTE) return t('comptabilite.flux.nonReparti');
    return segment.libelle ?? '';
  };

  const legende = useMemo(() => {
    const vus = new Map<string, SegmentBarre>();
    for (const barre of barres) {
      for (const segment of barre.segments) if (!vus.has(segment.id)) vus.set(segment.id, segment);
    }
    return [...vus.values()].sort((a, b) => (a.rang ?? 999) - (b.rang ?? 999));
  }, [barres]);

  const decrire = (barre: BarreMois): string => {
    const statut = t(
      `comptabilite.statut.${barre.provisoire ? 'provisoire' : 'cloture'}`,
    ).toLocaleLowerCase(f.langue);
    const base = t('comptabilite.historique.barre', {
      mois: f.moisLong(barre.mois),
      ht: f.euros(barre.htCentimes),
      statut,
    });
    if (associe) {
      const ma = barre.segments.find((s) => s.id === MA_PART)?.montantCentimes ?? 0;
      return `${base}, ${t('comptabilite.historique.maPartMontant', { montant: f.euros(ma) })}`;
    }
    return base;
  };

  const choisir = (evenement: KeyboardEvent, mois: string): void => {
    if (evenement.key !== 'Enter' && evenement.key !== ' ') return;
    evenement.preventDefault();
    onChoisir(mois);
  };

  const apercu = barres.find((b) => b.mois === (survol ?? selection));
  const labelPair = bande < 26;

  return (
    <Section
      id="historique"
      numero={numero}
      titre={t('comptabilite.historique.titre')}
      note={t('comptabilite.historique.note')}
    >
      <ul className={styles.legende} aria-label={t('comptabilite.historique.legende')}>
        {associe ? (
          <>
            <li>
              <i className={styles.cle} style={styleRang(0)} aria-hidden="true" />
              {t('comptabilite.historique.maPart')}
            </li>
            <li>
              <i className={`${styles.cle} ${styles.cleHt}`} aria-hidden="true" />
              {t('comptabilite.historique.htMois')}
            </li>
          </>
        ) : (
          legende.map((segment) => (
            <li key={segment.id}>
              <i className={styles.cle} style={styleRang(segment.rang)} aria-hidden="true" />
              {nomSegment(segment)}
            </li>
          ))
        )}
        <li>{t('comptabilite.historique.provisoire')}</li>
      </ul>
      <div ref={defilant} className={styles.historiqueDefile}>
        <svg
          className={styles.historiqueSvg}
          viewBox={`0 0 ${String(W)} ${String(H)}`}
          width={W}
          height={H}
          role="group"
          aria-label={t('comptabilite.historique.aria', { count: barres.length })}
        >
          {GRADUATIONS.map((g) => (
            <g key={g}>
              <line
                className={g === 0 ? styles.traitBase : styles.traitGrille}
                x1={mG}
                x2={W - mD}
                y1={y(haut * g).toFixed(1)}
                y2={y(haut * g).toFixed(1)}
              />
              <text
                className={styles.axeY}
                x={mG - 6}
                y={(y(haut * g) + 3).toFixed(1)}
                textAnchor="end"
              >
                {g === 0 ? '0' : f.axeEuros((haut * g) / 100)}
              </text>
            </g>
          ))}
          {barres.map((barre, i) => {
            const centre = mG + bande * i + bande / 2;
            const choisie = barre.mois === selection;
            const gauche = centre - largeurBarre / 2;
            const afficheLibelle = !labelPair || (barres.length - 1 - i) % 2 === 0;
            const annee =
              i === 0 || barre.mois.slice(5) === '01' || i === barres.length - 1
                ? barre.mois.slice(0, 4)
                : null;
            let cumul = 0;
            return (
              <g
                key={barre.mois}
                className={[styles.moisBarre, barre.provisoire ? styles.provisoire : ''].join(' ')}
                role="button"
                tabIndex={0}
                aria-pressed={choisie}
                aria-label={decrire(barre)}
                onClick={() => onChoisir(barre.mois)}
                onKeyDown={(evenement) => choisir(evenement, barre.mois)}
                onMouseEnter={() => setSurvol(barre.mois)}
                onMouseLeave={() => setSurvol(null)}
                onFocus={() => setSurvol(barre.mois)}
                onBlur={() => setSurvol(null)}
              >
                {choisie ? (
                  <rect
                    className={styles.selection}
                    x={(centre - bande / 2 + 1).toFixed(1)}
                    y={mH}
                    width={(bande - 2).toFixed(1)}
                    height={hauteurUtile}
                    rx="4"
                  />
                ) : null}
                {associe ? (
                  <rect
                    className={`${styles.barre} ${styles.barreHt}`}
                    x={gauche.toFixed(1)}
                    y={y(barre.htCentimes).toFixed(1)}
                    width={largeurBarre.toFixed(1)}
                    height={(y(0) - y(barre.htCentimes)).toFixed(1)}
                  />
                ) : null}
                {barre.segments.map((segment) => {
                  const bas = associe ? 0 : cumul;
                  const haute = y(bas + segment.montantCentimes);
                  const basse = y(bas);
                  cumul += segment.montantCentimes;
                  return (
                    <rect
                      key={segment.id}
                      className={styles.barre}
                      style={styleRang(segment.rang)}
                      x={gauche.toFixed(1)}
                      y={haute.toFixed(1)}
                      width={largeurBarre.toFixed(1)}
                      height={Math.max(basse - haute, 0.5).toFixed(1)}
                    />
                  );
                })}
                {afficheLibelle ? (
                  <text
                    className={`${styles.axeX} ${choisie ? styles.axeXChoisi : ''}`}
                    x={centre.toFixed(1)}
                    y={H - 20}
                    textAnchor="middle"
                  >
                    {f.moisCourt(barre.mois).replace('.', '')}
                  </text>
                ) : null}
                {annee ? (
                  <text
                    className={`${styles.axeX} ${styles.axeAnnee}`}
                    x={centre.toFixed(1)}
                    y={H - 6}
                    textAnchor="middle"
                  >
                    {annee}
                  </text>
                ) : null}
                <rect
                  className={styles.prise}
                  x={(centre - bande / 2).toFixed(1)}
                  y="0"
                  width={bande.toFixed(1)}
                  height={H}
                />
              </g>
            );
          })}
        </svg>
      </div>
      <p className={styles.lecture} aria-live="polite">
        {apercu ? (
          <>
            <strong>{f.moisLong(apercu.mois)}</strong>
            {' · '}
            {t(`comptabilite.statut.${apercu.provisoire ? 'provisoire' : 'cloture'}`)}
            {' · '}
            {t('comptabilite.historique.htLecture', { montant: f.euros(apercu.htCentimes) })}
            {apercu.segments.map((segment) => (
              <span key={segment.id}>
                {' · '}
                {nomSegment(segment)} {f.euros(segment.montantCentimes)}
              </span>
            ))}
          </>
        ) : null}
      </p>
    </Section>
  );
}
